import { Button, Cluster, Container, Pill, Section, Stack } from "@humaniti/ui";
import { Link, useSearchParams } from "react-router-dom";
import { SEO } from "../lib/seo";

/**
 * Single landing surface for any sign-in / sign-up CTA in the marketing
 * site. Until M1.6 launch, the requester + annotator SPAs are not feature-
 * complete enough to show to the public — every "Hire Humans" / "Earn as a
 * Human" button on the marketing site routes here instead of to the SPA
 * sign-in page. Once the SPAs are ready to advertise, repoint the CTAs back
 * to /app/sign-in and /annotate/sign-in (or wherever they should land) and
 * delete this page.
 *
 * Optional ?for query swaps a single line of copy so the page reads in
 * context for whichever side the visitor came from:
 *   /coming-soon            → neutral "Builders or Humans" framing
 *   /coming-soon?for=builders → "If you're a Builder…"
 *   /coming-soon?for=humans   → "If you're a Human…"
 *
 * Internal staff who need to test the actual sign-in flows reach them via
 * the admin SPA's "Sign-in flows" page — those URLs stay unlisted
 * publicly so casual visitors don't end up on an unfinished surface.
 */
export const ComingSoonPage = () => {
  const [params] = useSearchParams();
  const audience = params.get("for") as "builders" | "humans" | null;

  const lede =
    audience === "builders"
      ? "Builder workspace onboarding opens at launch. We'll send you the keys when the network is ready for production datasets."
      : audience === "humans"
        ? "Human qualification opens at launch. We'll send you the keys when the network is ready for paying work."
        : "Builder workspaces and Human qualification open at launch. We'll send you the keys when the network is ready.";

  return (
    <>
      <SEO
        title="Coming soon — Network launches at M1.6"
        description="The Humaniti network opens at launch. Builders and Humans can join the waitlist. Read the roadmap and the field notes meanwhile."
        path="/coming-soon"
      />
      <Section>
        <Container size="full">
          <Stack gap="2xl">
            <Pill variant="accent">Coming soon</Pill>
            <h1
              style={{
                margin: 0,
                fontFamily: "var(--font-sans)",
                fontWeight: 500,
                fontSize: "var(--fs-display)",
                lineHeight: "var(--lh-display)",
                letterSpacing: "-0.02em",
                maxWidth: 960,
              }}
            >
              Almost ready<span style={{ color: "var(--teal)" }}>.</span>
            </h1>
            <Stack gap="md" style={{ maxWidth: 620 }}>
              <p
                style={{
                  margin: 0,
                  fontSize: "var(--fs-body-lg)",
                  lineHeight: "var(--lh-body-lg)",
                }}
              >
                {lede}
              </p>
              <p
                style={{
                  margin: 0,
                  fontSize: "var(--fs-body-lg)",
                  lineHeight: "var(--lh-body-lg)",
                  color: "var(--fg-secondary)",
                }}
              >
                Verified Humans. Verified work. Direct pay. The network ships when the loop is end-
                to-end.
              </p>
            </Stack>
            <Cluster gap="lg">
              <a href="mailto:hello@humaniti.io?subject=Notify%20me%20at%20launch">
                <Button>Notify me at launch →</Button>
              </a>
              <Link to="/roadmap" style={{ textDecoration: "none" }}>
                <Button variant="ghost">Read the roadmap →</Button>
              </Link>
            </Cluster>
          </Stack>
        </Container>
      </Section>

      {/* Where to go now */}
      <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
        <Container size="full">
          <Stack gap="lg">
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "var(--fs-label)",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--fg-secondary)",
              }}
            >
              § What you can read now
            </span>
            <h2
              style={{
                margin: 0,
                fontFamily: "var(--font-sans)",
                fontWeight: 500,
                fontSize: "var(--fs-h1)",
                lineHeight: "var(--lh-h1)",
                letterSpacing: "-0.01em",
              }}
            >
              The work is in public<span style={{ color: "var(--teal)" }}>.</span>
            </h2>
            <p
              style={{
                margin: 0,
                fontSize: "var(--fs-body-lg)",
                lineHeight: "var(--lh-body-lg)",
                maxWidth: 720,
                color: "var(--fg-secondary)",
              }}
            >
              We are not in stealth. The protocol, the methodology, and the four-phase arc are
              already documented. Read what is built and what is coming.
            </p>
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {LINKS.map((l) => (
                <li
                  key={l.href}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "64px 1fr",
                    gap: "var(--space-5)",
                    padding: "var(--space-5) 0",
                    borderTop: "1px solid var(--border-hairline)",
                  }}
                >
                  <div
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: "var(--fs-small)",
                      color: "var(--fg-secondary)",
                      paddingTop: "var(--space-1)",
                    }}
                  >
                    {l.num}
                  </div>
                  <div>
                    <div
                      style={{
                        fontFamily: "var(--font-sans)",
                        fontWeight: 500,
                        fontSize: "20px",
                        lineHeight: "28px",
                        marginBottom: "var(--space-2)",
                      }}
                    >
                      <Link to={l.href} style={{ color: "var(--ink)", textDecoration: "none" }}>
                        {l.label} →
                      </Link>
                    </div>
                    <div
                      style={{
                        fontSize: "var(--fs-body)",
                        lineHeight: "var(--lh-body)",
                        color: "var(--fg-secondary)",
                        maxWidth: 640,
                      }}
                    >
                      {l.body}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </Stack>
        </Container>
      </Section>
    </>
  );
};

const LINKS = [
  {
    num: "01",
    label: "Roadmap",
    href: "/roadmap",
    body: "Network → Record → Economy → Commons. Four phases. No timelines. The arc tells the labor → ownership → governance through-line.",
  },
  {
    num: "02",
    label: "How it works",
    href: "/how-it-works",
    body: "Task lifecycle, Tier ladder, consensus mechanics, dispute flow. The protocol layer of the network, written down.",
  },
  {
    num: "03",
    label: "Manifesto",
    href: "/manifesto",
    body: "Why Humaniti exists. The labor problem, the inversion, the invitation. Long-form.",
  },
  {
    num: "04",
    label: "Field notes",
    href: "/notes",
    body: "Editorial — methodology, the public roadmap arc, the people doing the work.",
  },
];
